import { useEffect } from 'react';

import { isAuthenticatedUser } from 'js/lib/user';

import type { EnrollmentChoices } from 'bundles/enroll-common/components/xdp/withEnrollment';
import { policies, scopes } from 'bundles/enroll-common/utils/scopedContentRule';
import { useScopedContentRule } from 'bundles/enroll-common/utils/withScopedContentRule';
import useProductOwnerships from 'bundles/entitlements-common/hooks/useProductOwnerships';
import GoogleCertificateEpicClient from 'bundles/epic/clients/GoogleCertificate';
import { useGetLoggedInUserSubscriptions } from 'bundles/subscriptions-common/utils/useGetLoggedInUserSubscriptions';

// If a subscription was created before the launch of this test, the learner is not eligible for this test.
const ACTIVE_SUBSCRIPTIONS_CREATED_AT_CUTOFF_TIMESTAMP = 1741965540000;

// The "Start free trial" experiment will only apply to s12ns and courses-in-s12ns.
// Standalone courses, projects, and guided projects (all true values for (isStandAloneCourse)) are not eligible.
// This util is also used on articles and RDPs, so we assign a default value of false to isStandAloneCourse.
// so that we can safely exclude standalone courses without affecting articles.
export const useFreeTrialCTACopyExperimentData = ({
  enrollmentAvailableChoices,
  isStandAloneCourse = false,
  s12nId,
  courseId,
}: {
  enrollmentAvailableChoices?: EnrollmentChoices;
  isStandAloneCourse?: boolean;
  s12nId?: string;
  courseId?: string;
}) => {
  const { subscriptions, pending } = useGetLoggedInUserSubscriptions({ skip: !isAuthenticatedUser() });
  const { courseOwnership, s12nOwnership } = useProductOwnerships({ courseId, s12nId });
  // For articles pages, we need to use ScopedContentRule to determine if the course
  // is part of Coursera Plus or not.
  const { scopedContentRule } = useScopedContentRule({
    s12nId,
    courseId,
    scope: scopes.COURSERA_PLUS_SUBSCRIPTION,
    skip: courseOwnership?.owns || s12nOwnership?.owns,
  });

  const activeSubscriptionsBeforeTestLaunch = subscriptions?.filter((subscription) => {
    const isSubscriptionStartedBeforeTestLaunch =
      subscription?.startsAt && subscription.startsAt < ACTIVE_SUBSCRIPTIONS_CREATED_AT_CUTOFF_TIMESTAMP;

    return (
      isSubscriptionStartedBeforeTestLaunch &&
      (subscription.isActive || subscription.isFreeTrial || subscription.isCancelled)
    );
  });
  const hasActiveCourseraPlusOrLiteSubscription = subscriptions?.some(
    (subscription) =>
      (subscription.isCourseraPlusSubscription || subscription.isCourseraLiteSubscription) &&
      (subscription.isActive || subscription.isFreeTrial || subscription.isCancelled)
  );

  const hasActiveSubscriptionsBeforeTestLaunch = (activeSubscriptionsBeforeTestLaunch || []).length > 0;

  const isUserBasedFreeTrialCopyEnabled =
    isAuthenticatedUser() &&
    !isStandAloneCourse &&
    !courseOwnership?.owns &&
    !s12nOwnership?.owns &&
    !(enrollmentAvailableChoices?.canEnrollThroughCourseraPlus && hasActiveCourseraPlusOrLiteSubscription) &&
    !(scopedContentRule?.policy === policies.ALLOW && hasActiveCourseraPlusOrLiteSubscription) &&
    !hasActiveSubscriptionsBeforeTestLaunch &&
    (!enrollmentAvailableChoices || enrollmentAvailableChoices?.canEnrollThroughS12nSubscriptionFreeTrial) &&
    GoogleCertificateEpicClient.preview('elevateLTVStartFreeTrialCopyUserBasedEnabled');

  const isSessionBasedFreeTrialCopyEnabled =
    !isAuthenticatedUser() &&
    !isStandAloneCourse &&
    GoogleCertificateEpicClient.preview('elevateLTVStartFreeTrialCopySessionBasedEnabled');

  return {
    loading: pending || (isAuthenticatedUser() && subscriptions === undefined),
    hasActiveSubscriptionsBeforeTestLaunch,
    isUserBasedFreeTrialCopyEnabled,
    isSessionBasedFreeTrialCopyEnabled,
  };
};

export const useGenerateStartFreeTrialTestEpicImpression = ({ isStandaloneCourse = false }) => {
  const isUserLoggedIn = isAuthenticatedUser();
  const { hasActiveSubscriptionsBeforeTestLaunch, loading } = useFreeTrialCTACopyExperimentData({});

  useEffect(() => {
    if (!loading && !isStandaloneCourse) {
      if (!isUserLoggedIn) {
        // Session-Based Epic: https://tools.coursera.org/epic/experiment/Y_gGZfq4Ee-rpxIFmMac9w
        GoogleCertificateEpicClient.get('elevateLTVStartFreeTrialCopySessionBasedEnabled');
      }

      if (isUserLoggedIn && !hasActiveSubscriptionsBeforeTestLaunch) {
        // User-Based Epic: https://tools.coursera.org/epic/experiment/lXIxCPq4Ee-zTAr_84a0uw
        GoogleCertificateEpicClient.get('elevateLTVStartFreeTrialCopyUserBasedEnabled');
      }
    }
  }, [isUserLoggedIn, hasActiveSubscriptionsBeforeTestLaunch, isStandaloneCourse, loading]);

  return null;
};
